import { useEffect, useState } from "react";
import { Bell, Clock } from "lucide-react";
import { activity_task_path } from "@/lib/routes";
import { Avatar } from "@/components/ui/Avatar";
import { TimeAgo } from "@/components/TimeAgo";
import type { ActivityEvent } from "@/pages/Tasks/task";

// Business phrases for the audit feed — no state names, no system jargon.
// Unknown kinds fall back to a humanized label so new backend events render
// without a matching frontend deploy.
const PHRASE: Record<string, (actor: string | null) => string> = {
  created: () => "Task created",
  stage_entered: () => "Created",
  reminder_sent: () => "Reminder sent",
  escalation_sent: () => "Escalated",
  dependents_alerted: () => "Downstream teams notified",
  stall_reminder_sent: () => "Quiet-work reminder sent",
  nudged: (actor) => `${actor ?? "Someone"} sent a reminder`,
  claimed: (actor) => `${actor ?? "Someone"} took this on`,
  released: (actor) => `${actor ?? "Someone"} handed it back`,
  snoozed: () => "Reminders paused",
  resumed: () => "Reminders resumed",
  submitted: (actor) => (actor ? `${actor} submitted for review` : "Submitted for review"),
  approved: (actor) => (actor ? `${actor} approved` : "Approved"),
  changes_requested: (actor) => (actor ? `${actor} requested changes` : "Changes requested"),
  commented: (actor) => (actor ? `${actor} commented` : "Commented"),
  reopened: () => "Reopened",
  closed: () => "Task closed",
};

function phraseFor(event: ActivityEvent): string {
  // The assignee's name rides in `detail` (see Tasks::ActivitiesController#event_detail).
  if (event.kind === "assigned") {
    return `${event.actor_name ?? "Someone"} put ${event.detail ?? "someone"} on this`;
  }
  // "added X, removed Y as reviewer" comes ready-made in `detail`.
  if (event.kind === "people_changed") {
    return `${event.actor_name ?? "Someone"} ${event.detail ?? "changed who's on this"}`;
  }
  const build = PHRASE[event.kind];
  const base = build ? build(event.actor_name) : event.kind.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());
  // Collapsed fan-out ("Reminder sent to 7 people") — backend sends one
  // entry per send cycle with the recipient count.
  if (event.recipients && event.recipients > 1) return `${base} to ${event.recipients} people`;
  return base;
}

// The task's audit feed, shared by the Tasks drawer and the plan review
// page's Conversation tab. Fetches its own events (plain JSON, not Inertia).
// Bump refreshKey to refetch in place (the drawer does after a comment).
export default function TaskActivityFeed({ taskId, refreshKey = 0 }: { taskId: string; refreshKey?: number }) {
  const [events, setEvents] = useState<ActivityEvent[] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    setEvents(null);
    setError(false);
    const controller = new AbortController();

    fetch(activity_task_path(taskId), { signal: controller.signal, headers: { Accept: "application/json" } })
      .then(async (res) => {
        if (!res.ok) throw new Error("activity fetch failed");
        const json: { events: ActivityEvent[] } = await res.json();
        if (!controller.signal.aborted) setEvents(json.events);
      })
      .catch((err) => {
        if (err instanceof DOMException && err.name === "AbortError") return;
        setError(true);
      });

    return () => controller.abort();
  }, [taskId, refreshKey]);

  if (error) return <p className="text-xs text-muted-foreground">Couldn't load activity.</p>;
  if (events === null) return <p className="text-xs text-muted-foreground">Loading…</p>;
  if (events.length === 0) return <p className="text-xs text-muted-foreground">Nothing yet.</p>;

  return (
    <ul className="flex flex-col">
      {events.map((event, i) => (
        <li key={`${event.kind}-${event.at}-${i}`} className="flex flex-col py-1.5 text-xs">
          <div className="flex items-center gap-2.5">
            {event.actor_initials ? (
              <Avatar
                tone="primary"
                name={event.actor_name ?? ""}
                initials={event.actor_initials}
                avatarUrl={event.actor_avatar_url}
              />
            ) : (
              <span className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
                {event.kind.includes("reminder") || event.kind === "nudged" ? <Bell size={11} /> : <Clock size={11} />}
              </span>
            )}
            <span className="min-w-0">{phraseFor(event)}</span>
            <TimeAgo at={event.at} className="ml-auto shrink-0 text-[11px] text-muted-foreground" />
          </div>
          {/* A note is what someone wrote — a comment, a send-back reason, a
              reminder message. It is the content, so it gets a full card. */}
          {event.note && (
            <p className="ml-8 mt-1.5 whitespace-pre-line rounded-md border border-primary/20 bg-primary/5 px-3 py-2 text-[13px] leading-relaxed text-foreground">
              {event.note}
            </p>
          )}
        </li>
      ))}
    </ul>
  );
}
